<template>
  <div
    v-if="!isProd && hasOverrides"
    class="flag-override-indicator"
  >
    <v-menu
      v-model="expanded"
      :close-on-content-click="false"
      location="top end"
      offset="8"
    >
      <template #activator="{props}">
        <v-btn
          v-bind="props"
          data-testid="flag-override-indicator"
          icon="mdi-flag-variant"
          color="warning"
          size="small"
          elevation="4"
          :title="`Flag overrides active (${overrideEntries.length})`"
        />
      </template>
      <v-card min-width="260">
        <v-card-title class="text-subtitle-2 d-flex align-center">
          <v-icon
            start
            icon="mdi-flag-variant"
            color="warning"
          />
          Flag overrides active
        </v-card-title>
        <v-divider />
        <v-list density="compact">
          <v-list-item
            v-for="[key, value] in overrideEntries"
            :key="key"
          >
            <v-list-item-title class="text-body-2">
              <code>{{ key }}</code>
            </v-list-item-title>
            <template #append>
              <v-chip
                :color="value ? 'success' : 'error'"
                size="x-small"
                label
              >
                {{ value }}
              </v-chip>
              <v-btn
                icon="mdi-close"
                size="x-small"
                variant="text"
                :title="`Clear override for ${key}`"
                @click="clearOne(key)"
              />
            </template>
          </v-list-item>
        </v-list>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            size="small"
            variant="text"
            @click="clearAll"
          >
            Clear all
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import {OVERRIDE_STORAGE_KEY, OVERRIDE_CHANGE_EVENT, readOverrides} from '@/services/FlagService';

/**
 * This component shows an indicator when there are active flag overrides (for development/testing purposes).
 * It listens to changes in the overrides via a custom event and the localStorage, and allows clearing individual
 * or all overrides. Window events are used instead of a vuex store to keep the dev overrides out of app state.
 * It is only rendered in non-production environments.
 */

export default {
  name: 'FlagOverrideIndicator',
  data() {
    return {
      isProd: process.env.VUE_APP_FIREBASE_ENV === 'production',
      overrides: {},
      expanded: false,
    };
  },
  computed: {
    overrideEntries() {
      return Object.entries(this.overrides);
    },
    hasOverrides() {
      return this.overrideEntries.length > 0;
    },
  },
  mounted() {
    if (this.isProd) return;
    this.refresh();
    window.addEventListener(OVERRIDE_CHANGE_EVENT, this.refresh);
    window.addEventListener('storage', this.onStorage);
  },
  beforeUnmount() {
    window.removeEventListener(OVERRIDE_CHANGE_EVENT, this.refresh);
    window.removeEventListener('storage', this.onStorage);
  },
  methods: {
    refresh() {
      this.overrides = readOverrides();
    },
    onStorage(event) {
      if (event.key === OVERRIDE_STORAGE_KEY) {
        this.refresh();
      }
    },
    clearOne(key) {
      window.FlagOverrides?.clear(key);
    },
    clearAll() {
      window.FlagOverrides?.reset();
      this.expanded = false;
    },
  },
};
</script>

<style scoped>
.flag-override-indicator {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 3000;
}

code {
  font-size: 0.75rem;
}
</style>
